/**
 * landing.component.ts
 * --------------------
 * Page 1 — the first page the user sees after Entra ID authentication.
 *
 * Displays:
 *  - A welcome message with the user's display name (from MSAL account)
 *  - Two action buttons:
 *      "New Assessment"          → sets flow to 'new', navigates to /document-upload
 *      "Update Existing Assessment" → sets flow to 'existing', navigates to /application-details
 *
 * On load:
 *  - If the user reloaded the page and a previous session exists in
 *    sessionStorage, that session's blobs are deleted from the backend
 *    before the local state is cleared. This implements feature #2:
 *    "a new session should be created if user reloads the page and the
 *    earlier session files should be deleted."
 *
 * This page renders ONLY the header — no stepper nav bar.
 *
 * Access: protected by authGuard (only authenticated users reach here).
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule }       from '@angular/common';
import { Router }             from '@angular/router';
import { MsalService }        from '@azure/msal-angular';
import { AccountInfo }        from '@azure/msal-browser';
import { SessionService }     from '../../core/services/session.service';
import { UploadService }      from '../../core/services/upload.service';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss',
})
export class LandingComponent implements OnInit {
  /** Currently signed-in user — populated on init */
  account: AccountInfo | null = null;

  constructor(
    private readonly authService:    MsalService,
    private readonly sessionService: SessionService,
    private readonly uploadService:  UploadService,
    private readonly router:         Router,
  ) {}

  ngOnInit(): void {
    const accounts = this.authService.instance.getAllAccounts();
    this.account   = accounts.length > 0 ? accounts[0] : null;

    // Feature #2: If there is an existing session in this tab (e.g. the user
    // reloaded the page), delete its backend data before resetting local state.
    const prevSessionId = this.sessionService.getPreviousSessionId();
    if (prevSessionId) {
      this.uploadService.deleteSession(prevSessionId).subscribe({
        error: (err) =>
          console.warn(`Could not delete previous session ${prevSessionId}:`, err),
        complete: () => console.log(`Previous session ${prevSessionId} deleted.`),
      });
    }

    this.sessionService.reset();
  }

  /** Returns the user's first name or full display name */
  get userName(): string {
    if (!this.account) return '';
    const name = this.account.name ?? this.account.username ?? '';

    // Entra ID may return names in "Last, First" format (e.g. "Tiwari, Gyanendra").
    // If a comma is present, extract the first name from after the comma.
    if (name.includes(',')) {
      const firstName = name.split(',')[1]?.trim() ?? '';
      return firstName.split(' ')[0]; // take first token in case of middle names
    }

    // Standard "First Last" format — use the first token
    return name.split(' ')[0];
  }

  /** Handler for "New Assessment" button */
  startNewAssessment(): void {
    this.sessionService.startNewAssessment();
    this.router.navigate(['/document-upload']);
  }

  /** Handler for "Update Existing Assessment" button */
  startExistingAssessment(): void {
    this.sessionService.startExistingAssessment();
    this.router.navigate(['/application-details']);
  }
}

